import React from 'react'
import * as PropTypes from 'prop-types'
import styled from 'styled-components'
import { Box, Flex } from 'reflexbox/styled-components'

import CircleIcon from '@anvilco-pkg/ui/components/CircleIcon'

import { H4 } from 'lib/ui/primitives'
import media from 'media'
import theme from 'theme'

import SubSection from 'components/page/Section/SubSection'
import UniversalLink from 'components/UniversalLink'

const DeveloperApproachInner = styled(Box)`
  position: relative;
  z-index: 10;
`

export const StyledDeveloperApproachTitle = styled(H4).attrs({
  mb: ['32px', '32px', '48px'],
  as: 'h2',
})``

const StyledDeveloperApproachList = styled(Box)`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 28px;

  ${media.desktop`
    grid-template-columns: 1fr 1fr;
  `}

  ${media.giant`
    grid-template-columns: 1fr 1fr 1fr;
    grid-gap: 36px;
  `}
`

const DeveloperApproachItemContainer = styled(Flex)`
  gap: 16px;
`

const StyledCircleIcon = styled(CircleIcon)`
  && {
    margin-right: 0;
    width: 46px;
    height: 46px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`

const Eyebrow = styled.div`
  font-size: 18px;
  font-weight: 600;
  margin-top: 0;
  margin-bottom: 18px;
`

const DeveloperApproachSubTitle = styled(Eyebrow).attrs({
  fontSize: ['18px'],
})`
  text-decoration: underline;
  transition: color 0.2s;
  cursor: pointer;
  margin-bottom: 4px;
`

const DeveloperApproachParagraph = styled(Box).withConfig({
  shouldForwardProp: (prop) => ['mode'].indexOf(prop) === -1,
})`
  ${({ mode }) => mode === 'dark' && `color: ${theme.colors.white};`}
`

const StyledLink = styled(UniversalLink).withConfig({
  shouldForwardProp: (prop) => ['mode'].indexOf(prop) === -1,
})`
  /* TODO: increased specificity comes from SubSection styles - we need to find a way to lower those */
  &&& {
    ${({ mode }) =>
      mode === 'dark'
        ? `
      color: ${theme.colors.white};
      :hover {
        color: ${theme.colors.linkColor.dark};
      }
      `
        : `
      color: ${theme.colors.black};
      :hover {
        color: ${theme.colors.linkColor.light};
      }
    `}
  }
`

const DeveloperApproachItem = ({ title, description, icon, to, mode }) => {
  const modeProps =
    mode === 'dark'
      ? { background: theme.colors.black, color: theme.colors.greens[30] }
      : { background: theme.colors.greens[10], color: theme.colors.black }
  return (
    <DeveloperApproachItemContainer>
      <StyledCircleIcon {...modeProps}>{icon}</StyledCircleIcon>
      <Box>
        <DeveloperApproachSubTitle>
          <StyledLink mode={mode} to={to}>
            {title}
          </StyledLink>
        </DeveloperApproachSubTitle>
        <DeveloperApproachParagraph mode={mode}>
          {description}
        </DeveloperApproachParagraph>
      </Box>
    </DeveloperApproachItemContainer>
  )
}

DeveloperApproachItem.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  icon: PropTypes.node,
  to: PropTypes.string,
  mode: PropTypes.oneOf(['light', 'dark']),
}

const DeveloperApproachSubSection = ({
  items = [],
  title = 'A developer-first approach to paperwork',
  mode = 'light',
  hideTitle = false,
  ...others
}) => {
  return (
    <SubSection
      mode={mode}
      vignette={
        mode === 'dark' && {
          // NOTE: this is a one-off color, not a theme color
          color: '#464A4E',
        }
      }
      {...others}
    >
      <DeveloperApproachInner>
        {!hideTitle && (
          <StyledDeveloperApproachTitle>{title}</StyledDeveloperApproachTitle>
        )}
        <StyledDeveloperApproachList>
          {items.map((item, index) => (
            <DeveloperApproachItem {...item} key={index} mode={mode} />
          ))}
        </StyledDeveloperApproachList>
      </DeveloperApproachInner>
    </SubSection>
  )
}

DeveloperApproachSubSection.propTypes = {
  mode: PropTypes.oneOf(['light', 'dark']),
  title: PropTypes.node,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      icon: PropTypes.node,
      to: PropTypes.string,
    })
  ),
  hideTitle: PropTypes.bool,
}

export default DeveloperApproachSubSection
